import { forwardRef, useId } from 'react'
import * as SelectPrimitive from '@radix-ui/react-select'
import { Check, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useWorkScaleClass } from '@/design-system/work-scale'

/**
 * Select — раскрывающийся выбор (`select` макета) на Radix: доступность и навигация с клавиатуры
 * из коробки. Триггер стилизован как Input; состояния focus/disabled/invalid из токенов.
 * Композиция в стиле shadcn: Select / SelectTrigger / SelectValue / SelectContent / SelectItem.
 */
export const Select = SelectPrimitive.Root
export const SelectGroup = SelectPrimitive.Group
export const SelectValue = SelectPrimitive.Value

export interface SelectTriggerProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> {
  invalid?: boolean
}

export const SelectTrigger = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(({ className, invalid, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    aria-invalid={invalid}
    className={cn(
      'flex w-full items-center justify-between gap-2 rounded-8 border bg-white px-3 py-2 text-14 text-txt outline-none transition-colors',
      'data-[placeholder]:text-txt-tertiary',
      'hover:border-line-strong focus-visible:ring-2 focus-visible:ring-accent',
      'disabled:cursor-not-allowed disabled:bg-subtle disabled:opacity-50',
      invalid ? 'border-red focus-visible:ring-red' : 'border-line-strong',
      className,
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="size-4 shrink-0 stroke-current text-txt-secondary" aria-hidden />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
))
SelectTrigger.displayName = 'SelectTrigger'

export const SelectContent = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => {
  // Шкала шрифтов (centrika-docs!31): портал вне DOM-поддерева рабочей области, поэтому класс
  // увеличенной шкалы объявляется на нём — но только когда список открыт из кабинета. Тот же
  // примитив используется полем «Пол» на странице регистрации, которая остаётся базовой.
  const workScale = useWorkScaleClass()
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        position={position}
        className={cn(
          // z-popover (не z-sticky): портал рендерится в document.body и должен быть виден/кликабелен
          // поверх открытого Dialog (z-overlay=200), иначе список рисуется под панелью диалога и
          // клики перехватывает диалог (замечание РП №3, p2-remarks-2).
          'ds-surface z-popover max-h-72 min-w-32 overflow-hidden rounded-8 shadow-elevated',
          workScale,
          className,
        )}
        {...props}
      >
        <SelectPrimitive.Viewport className="p-1">{children}</SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
})
SelectContent.displayName = 'SelectContent'

/**
 * Разделительная черта внутри списка — отделяет пункт-действие («+ Добавить …», первым элементом)
 * от собственно значений справочника. Введена требованием centrika-docs
 * `page.create-sout-order.new-workplace` (MR !8). Тот же паттерн у полей выбора сотрудника, но
 * они живут в отдельном combobox'е с поиском `company/employee-select.tsx` (общее требование
 * centrika-docs, common.md, MR !22) — со своей чертой того же вида.
 */
export const SelectSeparator = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    // Radix помечает разделитель `aria-hidden` (декоративный) — роли `separator` у него нет,
    // поэтому для тестов порядка элементов списка оставляем стабильный `data-testid`.
    data-testid="select-separator"
    className={cn('-mx-1 my-1 h-px bg-line', className)}
    {...props}
  />
))
SelectSeparator.displayName = 'SelectSeparator'

/**
 * `hint` — вторая строка варианта менее заметным текстом (например ИНН под наименованием
 * клиента, centrika-docs!48). Рендерится ВНЕ `ItemText`: в закрытом поле Radix показывает
 * только содержимое `ItemText`, поэтому подпись остаётся в списке и не удваивает строку
 * в самом поле. Имя варианта Radix собирает из `ItemText`, поэтому подпись подключается
 * через `aria-describedby` — иначе скринридер её не услышал бы вовсе.
 */
export const SelectItem = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & { hint?: React.ReactNode }
>(({ className, children, hint, ...props }, ref) => {
  const hintId = useId()
  return (
  <SelectPrimitive.Item
    ref={ref}
    aria-describedby={hint ? hintId : undefined}
    className={cn(
      'relative flex cursor-pointer select-none flex-col items-start rounded-7 py-2 pl-8 pr-3 text-14 text-txt outline-none',
      'data-[highlighted]:bg-accent-bg data-[highlighted]:text-accent-txt',
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className,
    )}
    {...props}
  >
    <span className="absolute left-2 top-2 flex items-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="size-4 stroke-current" aria-hidden />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    {hint ? (
      <span id={hintId} className="text-12 text-txt-tertiary">
        {hint}
      </span>
    ) : null}
  </SelectPrimitive.Item>
  )
})
SelectItem.displayName = 'SelectItem'
